import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Phone, Lock, GraduationCap, ArrowRight, Loader2, Home, AlertCircle, CheckCircle2 } from 'lucide-react';
import ImportantInstructionsCard from '../components/ImportantInstructionsCard';
import './Signup.css';

const Signup = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [educational_level_id, setEducationalLevel] = useState('');
  const [response, setResponse] = useState(null);
  const [error, setError] = useState('');
  const [showInstructions, setShowInstructions] = useState(true);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== passwordConfirmation) {
      setError('كلمتا المرور غير متطابقتين 🚫');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/signup`, {
        name: `${firstName} ${lastName}`,
        phone,
        password,
        educational_level_id: educational_level_id,
      });

      setResponse(res.data);
      setTimeout(() => navigate('/Login'), 2000); // الانتقال بعد ثانيتين لرؤية رسالة النجاح
    } catch (error) {
      setError('فشل التسجيل: برجاء التأكد من البيانات المدخلة');
    } finally {
      setLoading(false);
    }
  };

  const goHome = () => {
    navigate('/');
  };

  return (
    <div className="split-login-container relative">

      {/* نافذة التعليمات المهمة */}
      <AnimatePresence>
        {showInstructions && (
          <motion.div
            className="instructions-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ImportantInstructionsCard onClose={() => setShowInstructions(false)} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* النصف الأيسر: الصورة والهوية */}
      <div className="login-left-side" style={{ background: "url('/images/person1.png') center/cover no-repeat" }}>
        <div className="overlay-gradient"></div>
        <motion.div
          className="brand-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h1>Edu <span>Masr</span></h1>
          <p>خطوتك الأولى نحو التفوق. أنشئ حسابك الآن وانضم إلى آلاف الطلاب المتميزين.</p>
        </motion.div>
      </div>

      {/* النصف الأيمن: فورم التسجيل */}
      <div className="login-right-side scrollable-right">
        <motion.div
          className="form-wrapper signup-wrapper"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="form-header">
            <h2>إنشاء حساب جديد</h2>
            <p>سجل بياناتك للبدء في رحلتك التعليمية</p>
          </div>

          <form onSubmit={handleSubmit} className="modern-login-form">

            {/* الصف الأول: الاسم */}
            <div className="form-row">
              <div className="input-group">
                <label>الاسم الأول</label>
                <div className="input-with-icon">
                  <User size={18} className="input-icon" />
                  <input
                    type="text"
                    placeholder="محمد"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
              </div>
              <div className="input-group">
                <label>اسم العائلة</label>
                <div className="input-with-icon">
                  <User size={18} className="input-icon" />
                  <input
                    type="text"
                    placeholder="حسين"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* الصف الثاني: رقم الهاتف والمرحلة */}
            <div className="form-row">
              <div className="input-group">
                <label>رقم الهاتف المحمول</label>
                <div className="input-with-icon">
                  <Phone size={18} className="input-icon" />
                  <input
                    type="text"
                    placeholder="01xxxxxxxxx"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
              </div>
              <div className="input-group">
                <label>المرحلة الدراسية</label>
                <div className="input-with-icon">
                  <GraduationCap size={18} className="input-icon" />
                  <select
                    value={educational_level_id}
                    onChange={(e) => setEducationalLevel(e.target.value)}
                    required
                    disabled={loading}
                    className="select-input"
                  >
                    <option value="" disabled>اختر المرحلة</option>
                    <option value="1">الصف الاول الثانوي</option>
                    <option value="2">الصف الثاني الثانوي</option>
                    <option value="3">الصف الثالث الثانوي</option>
                    <option value="4">الصف الاول الاعدادي</option>
                    <option value="6">الصف الثاني الاعدادي</option>
                    <option value="7">الصف الثالث الاعدادي</option>
                    <option value="10">الصف الرابع الإبتدائي</option>
                    <option value="11">الصف الخامس الإبتدائي</option>
                    <option value="12">الصف السادس الإبتدائي</option>

                  </select>
                </div>
              </div>
            </div>

            {/* الصف الثالث: كلمة المرور */}
            <div className="form-row">
              <div className="input-group">
                <label>كلمة المرور</label>
                <div className="input-with-icon">
                  <Lock size={18} className="input-icon" />
                  <input
                    type="password"
                    placeholder="********"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
              </div>
              <div className="input-group">
                <label>تأكيد كلمة المرور</label>
                <div className="input-with-icon">
                  <Lock size={18} className="input-icon" />
                  <input
                    type="password"
                    placeholder="********"
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* التنبيهات */}
            <AnimatePresence>
              {error && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="error-alert">
                  <AlertCircle size={18} /> <span>{error}</span>
                </motion.div>
              )}
              {response && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="success-alert">
                  <CheckCircle2 size={18} /> <span>{response.message || 'تم التسجيل بنجاح! جاري تحويلك... 🎉'}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* الأزرار */}
            <div className="actions-row">
              <button type="submit" className="btn-primary-login" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="spinner" size={20} />
                    <span>جاري التسجيل...</span>
                  </>
                ) : (
                  <>
                    <span>إنشاء حساب</span>
                    <ArrowRight size={20} />
                  </>
                )}
              </button>

              <button type="button" className="btn-secondary-home" onClick={goHome} disabled={loading}>
                <Home size={18} />
              </button>
            </div>

          </form>

          <div className="form-footer">
            <p>لديك حساب بالفعل؟ <a href="/Login">تسجيل الدخول</a></p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Signup;